import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  BlockStack,
  Box,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Layout,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonThumbnail,
} from '@shopify/polaris'
import {
  ROUTE_MARKETS,
  ROUTE_PREFERENCES,
  ROUTE_SUBSCRIPTION,
} from '@Shared/constants/routes'
import SkeletonPageLoader from './SkeletonPageLoader'
import MainLayout from '@Layouts/MainLayout'
import MarketsIcon from './MarketsIcon'

// ── Markets ───────────────────────────────────────────────────────────────────

function MarketsSkeletonCard({ rows }: { rows: number }) {
  return (
    <Card padding="0">
      <Box
        paddingInlineStart="400"
        paddingInlineEnd="400"
        paddingBlockStart="300"
        paddingBlockEnd="300"
        background="bg-surface-secondary"
        borderBlockEndWidth="025"
        borderColor="border-secondary"
      >
        <InlineStack gap="300" blockAlign="center">
          <SkeletonThumbnail size="small" />
          <BlockStack gap="0">
            <Box width="130px">
              <SkeletonDisplayText size="small" />
            </Box>
            <Box width="80px">
              <SkeletonBodyText lines={1} />
            </Box>
          </BlockStack>
        </InlineStack>
      </Box>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          paddingInlineStart="400"
          paddingInlineEnd="400"
          paddingBlockStart="400"
          paddingBlockEnd="400"
          borderBlockEndWidth={i === rows - 1 ? '0' : '025'}
          borderColor="border-secondary"
        >
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="300" blockAlign="center">
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'var(--p-color-bg-fill-tertiary)',
                  flexShrink: 0,
                }}
              />
              <BlockStack gap="100">
                <Box width="150px">
                  <SkeletonBodyText lines={1} />
                </Box>
                <Box width="100px">
                  <SkeletonBodyText lines={1} />
                </Box>
              </BlockStack>
            </InlineStack>
            <Box width="110px">
              <SkeletonBodyText lines={1} />
            </Box>
          </InlineStack>
        </Box>
      ))}
    </Card>
  )
}

export function MarketsSkeleton() {
  const { t: translate } = useTranslation()
  return (
    <MainLayout
      title={translate('Prisync.markets.label')}
      titleMetadata={<MarketsIcon />}
      subtitle={translate('Prisync.markets.subtitle')}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="300">
            <MarketsSkeletonCard rows={1} />
            <MarketsSkeletonCard rows={2} />
            <MarketsSkeletonCard rows={1} />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </MainLayout>
  )
}

// ── Preferences ───────────────────────────────────────────────────────────────

function PreferencesSkeleton() {
  const { t: translate } = useTranslation()

  const SkeletonField = () => (
    <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
      <Box
        paddingInlineStart={{ xs: '400', sm: '0' }}
        paddingInlineEnd={{ xs: '400', sm: '0' }}
      >
        <BlockStack gap="400">
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </Box>
      <Card roundedAbove="sm">
        <SkeletonBodyText lines={3} />
      </Card>
    </InlineGrid>
  )

  return (
    <MainLayout title={translate('Prisync.settings.label')}>
      <Layout>
        <Layout.Section>
          <BlockStack gap={{ xs: '800', sm: '400' }}>
            <SkeletonField />
            <Divider />
            <SkeletonField />
            <Divider />
            <SkeletonField />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </MainLayout>
  )
}

// ── Subscription ──────────────────────────────────────────────────────────────

function SubscriptionSkeleton() {
  const { t: translate } = useTranslation()
  return (
    <MainLayout title={translate('Prisync.subscription.label')}>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </MainLayout>
  )
}

// ── Route-aware entry point ───────────────────────────────────────────────────

function RouteAwareSkeleton(): JSX.Element {
  const { pathname } = useLocation()

  switch (pathname) {
    case ROUTE_MARKETS:
      return <MarketsSkeleton />
    case ROUTE_PREFERENCES:
      return <PreferencesSkeleton />
    case ROUTE_SUBSCRIPTION:
      return <SubscriptionSkeleton />
    default:
      return <SkeletonPageLoader />
  }
}

export default RouteAwareSkeleton
